'use client';
// adjust path as needed
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { auth } from '@/lib/firebaseConfig';
import {
  clearAuthState,
  logout,
  providerAuth,
  refreshUser,
  updateUser,
} from '@/store/slices/authSlice';
import { ProviderType } from '@/features/auth/types/http/responses/loginResponse';
import React from 'react';
import {
  browserLocalPersistence,
  setPersistence,
  signInWithCustomToken,
  User,
} from 'firebase/auth';
import { UpdateRequest } from '@/features/auth/types/http/requests/updateRequest';
import { authService } from '@/features/auth/services/authService';
import { clearPlan } from '@/store/slices/planSlice';
import { usePathname, useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { clearPrices } from '@/store/slices/paymentSlice';

const useAuth = () => {
  const dispatch = useAppDispatch();
  const [fbUser, setFbUser] = React.useState<User | null>(auth.currentUser);
  const { user, error, loading, changePasswordLoading } = useAppSelector(
    (store) => store.auth
  );
  const router = useRouter();
  const pathname = usePathname();
  const { data: session } = useSession();

  React.useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setFbUser(user);
      setPersistence(auth, browserLocalPersistence);
      if (!user) {
        handleClearAuth();
      }
    });

    return () => unsubscribe();
  }, []);

  const handleProviderAuth = async (providerType: ProviderType) => {
    try {
      const result = await dispatch(providerAuth(providerType)).unwrap();
      if (pathname === '/') {
        router.push('/app');
      }
      return result;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const handleUpdateUser = async (userData: UpdateRequest) => {
    return dispatch(updateUser(userData)).unwrap();
  };

  const handleLogout = () => {
    if (pathname === '/app') {
      router.replace('/?app=y');
    }
    handleClearAuth();
    dispatch(clearPlan());
    dispatch(clearPrices());

    const performAsyncLogout = async () => {
      try {
        await dispatch(logout({ uid: user?.uid! })).unwrap();
        await auth.signOut();
        console.log('pathname', pathname);

        if (pathname === '/app') {
          router.replace('/');
        }
      } catch (err) {
        console.error('Background logout failed:', err);
      }
    };

    performAsyncLogout();
  };

  const handleClearAuth = () => {
    dispatch(clearAuthState());
  };

  const refreshFbUser = async () => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('No user found');
      }
      await currentUser.reload();

      await currentUser.getIdToken(true);
      setFbUser(currentUser);
      return currentUser;
    } catch (error) {
      console.error('Error refreshing auth:', error);
      throw error;
    }
  };

  const userRef = React.useRef(user);
  React.useEffect(() => {
    userRef.current = user;
  }, [user]);

  const handleRefreshModelUser = async () => {
    return dispatch(refreshUser({ uid: userRef.current?.uid! })).unwrap();
  };

  const loginWithCustomToken = async () => {
    try {
      const token = await authService.getCustomToken(user?.uid!);
      const userCredential = await signInWithCustomToken(
        auth,
        token.data.token
      );

      setFbUser(userCredential.user);
    } catch (error) {
      console.error('Error signing in with custom token:', error);
      throw error;
    }
  };

  /**
   * Delete the user's account in Firebase Auth and the user in the DB.
   * @param fbUser The Firebase user object <Recently Logged In !!>
   * @throws An error if the user can't be deleted
   */
  const handleDeleteUser = async (fbUser: User) => {
    try {
      await fbUser?.delete();
      await authService.deleteUser({ uid: user?.uid! });
      handleClearAuth();
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  };
  return {
    auth,
    user,
    fbUser,
    error,
    loading,
    changePasswordLoading,
    nextAuthSession: session,

    providerAuth: handleProviderAuth,
    logout: handleLogout,
    updateUser: handleUpdateUser,
    refreshModelUser: handleRefreshModelUser,
    clearUser: handleClearAuth,
    refreshFbUser,
    loginWithCustomToken,
    deleteUser: handleDeleteUser,
  };
};

export default useAuth;
